"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Plus, RefreshCw, Waypoints } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { endOfMonth, startOfMonth } from "date-fns";
import { bySchedule, iso, type CurrentUser, type Employee, type Itinerary, type Team } from "@routecore/core";
import TeamModal, { type TeamFormData } from "@routecore/team-modal";
import { IntroCurtain, Reveal } from "@routecore/motion-fx";
import Sidebar, { MobileNav, type TabId } from "@routecore/sidebar";
import {
  DayModal,
  EmployeeModal,
  ItineraryModal,
  type AdminAccount,
  type EmployeeFormData,
  type ItineraryFormData,
} from "@routecore/overlays";
import { Toasts, type Toast } from "@routecore/toasts";
import CalendarView from "@routecore/views/calendar-view";
import TodayView from "@routecore/views/today-view";
import ReportsView from "@routecore/views/reports-view";
import RecordsView from "@routecore/views/records-view";

/* --------------------------------- context --------------------------------- */

type ModalState =
  | { kind: "employee"; employee: Employee | null }
  | {
      kind: "itinerary";
      itinerary: Itinerary | null;
      presetDate?: string;
      presetEmployeeId?: number;
      returnToDay?: string;
    }
  | { kind: "day"; date: string }
  | { kind: "teams" }
  | null;

type StudioCtx = {
  employees: Employee[];
  itineraries: Itinerary[];
  teams: Team[];
  currentUser: CurrentUser | null;
  isPrivileged: boolean;
  canEditEmployee: (emp: Pick<Employee, "id" | "boundAccountId" | "boundAccountEmail">) => boolean;
  canEditItinerary: (it: Pick<Itinerary, "employeeId">) => boolean;
  openTeamModal: () => void;
  toast: (message: string, tone?: "ok" | "info" | "warn" | "error") => void;
  addEmployee: (data: EmployeeFormData) => Promise<void>;
  updateEmployee: (id: number, data: EmployeeFormData) => Promise<void>;
  removeEmployee: (id: number) => Promise<void>;
  addItinerary: (data: ItineraryFormData) => Promise<void>;
  updateItinerary: (
    id: number,
    patch: Partial<ItineraryFormData>,
    opts?: { silent?: boolean },
  ) => Promise<void>;
  removeItinerary: (id: number) => Promise<void>;
  openEmployeeModal: (employee?: Employee) => void;
  openItineraryModal: (opts?: {
    itinerary?: Itinerary;
    presetDate?: string;
    presetEmployeeId?: number;
    returnToDay?: string;
  }) => void;
  openDayModal: (date: string) => void;
};

const Ctx = createContext<StudioCtx | null>(null);

export function useStudio() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStudio must be used within AppShell");
  return ctx;
}

/* ---------------------------------- shell ---------------------------------- */

function readStoredUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.id || !parsed.email) return null;
    return parsed as CurrentUser;
  } catch {
    return null;
  }
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init?.headers as Record<string, string> | undefined),
    };
    const token = localStorage.getItem("token");
    if (token) headers.Authorization = `Bearer ${token}`;
    const method = (init?.method || "GET").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const csrf = localStorage.getItem("csrf_token");
      if (csrf) headers["X-CSRF-Token"] = csrf;
    }
    const res = await fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    });
    if (res.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
      throw new Error(body?.error ?? body?.detail ?? `Request failed (${res.status})`);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timeoutId);
  }
}

export default function AppShell() {
  const [tab, setTab] = useState<TabId>("calendar");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [employeeAccounts, setEmployeeAccounts] = useState<AdminAccount[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(readStoredUser);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [modal, setModal] = useState<ModalState>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, tone: Toast["tone"] = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2800);
  }, []);

  const load = useCallback(async () => {
    try {
      const [emps, items, tms, empAccts] = await Promise.all([
        api<Employee[]>("/api/routecore/employees"),
        api<Itinerary[]>("/api/routecore/itineraries"),
        api<Team[]>("/api/routecore/teams"),
        api<AdminAccount[]>("/api/employees-for-bind").catch(() => []),
      ]);
      setEmployees(emps);
      setItineraries(items);
      setTeams(tms);
      setEmployeeAccounts(empAccts);
      setCurrentUser(readStoredUser());
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    load();
    const onStorage = () => setCurrentUser(readStoredUser());
    window.addEventListener("storage", onStorage);
    const onInterval = window.setInterval(onStorage, 15000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(onInterval);
    };
  }, [load]);

  /* Minimum curtain time so the intro never flickers on a fast connection. */
  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1250);
    return () => clearTimeout(t);
  }, []);

  const booting = phase === "loading" || !minElapsed;
  const [introPlayed, setIntroPlayed] = useState(false);
  useEffect(() => {
    if (!booting) {
      const t = setTimeout(() => setIntroPlayed(true), 700);
      return () => clearTimeout(t);
    }
  }, [booting]);

  /* ------------------------------- auth rules ------------------------------- */

  const isPrivileged = useMemo(
    () => !!currentUser && (currentUser.role === "admin" || !!currentUser.is_super_admin),
    [currentUser],
  );
  const isSuperAdmin = useMemo(
    () => !!currentUser?.is_super_admin,
    [currentUser],
  );

  const canEditEmployee = useCallback(
    (emp: Pick<Employee, "id" | "boundAccountId" | "boundAccountEmail">) => {
      if (isPrivileged) return true;
      if (!currentUser) return false;
      if (emp.boundAccountId && currentUser.id && emp.boundAccountId === currentUser.id) return true;
      // Email fallback match (if boundAccountEmail was denormalized but id missing).
      if (
        !emp.boundAccountId &&
        emp.boundAccountEmail &&
        currentUser.email &&
        emp.boundAccountEmail.toLowerCase() === currentUser.email.toLowerCase()
      ) {
        return true;
      }
      return false;
    },
    [currentUser, isPrivileged],
  );

  const canEditItinerary = useCallback(
    (it: Pick<Itinerary, "employeeId">) => {
      if (isPrivileged) return true;
      const emp = employees.find((e) => e.id === it.employeeId);
      if (!emp) return false;
      return canEditEmployee(emp);
    },
    [employees, canEditEmployee, isPrivileged],
  );

  /* ------------------------------- mutations ------------------------------- */

  const joinMeta = useCallback(
    (employeeId: number) => {
      const emp = employees.find((e) => e.id === employeeId);
      return { employeeName: emp?.name ?? null, employeeColor: emp?.color ?? null };
    },
    [employees],
  );

  const addEmployee = useCallback(
    async (data: EmployeeFormData) => {
      if (!isPrivileged) {
        toast("Not authorized to add employees", "warn");
        return;
      }
      const row = await api<Employee>("/api/routecore/employees", {
        method: "POST",
        body: JSON.stringify(data),
      });
      setEmployees((cur) => [...cur, row]);
      toast(`${row.name} joined the roster`);
    },
    [isPrivileged, toast],
  );

  const updateEmployee = useCallback(
    async (id: number, data: EmployeeFormData) => {
      const existing = employees.find((e) => e.id === id);
      if (!canEditEmployee(existing ?? { id, boundAccountId: null, boundAccountEmail: null })) {
        toast("Not authorized to edit this profile", "warn");
        return;
      }
      const row = await api<Employee>(`/api/routecore/employees/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      setEmployees((cur) => cur.map((e) => (e.id === id ? row : e)));
      setItineraries((cur) =>
        cur.map((i) =>
          i.employeeId === id ? { ...i, employeeName: row.name, employeeColor: row.color } : i,
        ),
      );
      toast("Profile updated");
    },
    [employees, canEditEmployee, toast],
  );

  const removeEmployee = useCallback(
    async (id: number) => {
      const existing = employees.find((e) => e.id === id);
      if (!canEditEmployee(existing ?? { id, boundAccountId: null, boundAccountEmail: null })) {
        toast("Not authorized to remove this employee", "warn");
        return;
      }
      const target = employees.find((e) => e.id === id);
      await api(`/api/routecore/employees/${id}`, { method: "DELETE" });
      setEmployees((cur) => cur.filter((e) => e.id !== id));
      setItineraries((cur) => cur.filter((i) => i.employeeId !== id));
      toast(`${target?.name ?? "Employee"} removed`, "info");
    },
    [employees, canEditEmployee, toast],
  );

  const addItinerary = useCallback(
    async (data: ItineraryFormData) => {
      const emp = employees.find((e) => e.id === data.employeeId);
      if (!canEditEmployee(emp ?? { id: data.employeeId, boundAccountId: null, boundAccountEmail: null })) {
        toast("Not authorized to add itineraries for this employee", "warn");
        return;
      }
      const row = await api<Omit<Itinerary, "employeeName" | "employeeColor">>(
        "/api/routecore/itineraries",
        { method: "POST", body: JSON.stringify(data) },
      );
      setItineraries((cur) => [...cur, { ...row, ...joinMeta(row.employeeId) }]);
      toast("Itinerary pinned to the calendar");
    },
    [employees, canEditEmployee, joinMeta, toast],
  );

  const updateItinerary = useCallback(
    async (id: number, patch: Partial<ItineraryFormData>, opts?: { silent?: boolean }) => {
      const existing = itineraries.find((i) => i.id === id);
      if (!canEditItinerary(existing ?? { employeeId: 0 })) {
        toast("Not authorized to update this itinerary", "warn");
        return;
      }
      // Cross-check the new target employee too, if reassigning.
      if (patch.employeeId != null && patch.employeeId !== existing?.employeeId) {
        const newEmp = employees.find((e) => e.id === patch.employeeId);
        if (!canEditEmployee(newEmp ?? { id: patch.employeeId, boundAccountId: null, boundAccountEmail: null })) {
          toast("Not authorized to reassign to that employee", "warn");
          return;
        }
      }
      const prev = itineraries;
      setItineraries((cur) =>
        cur.map((i) => {
          if (i.id !== id) return i;
          const next = { ...i, ...patch };
          if (patch.employeeId) Object.assign(next, joinMeta(patch.employeeId));
          return next;
        }),
      );
      try {
        await api(`/api/routecore/itineraries/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
        if (!opts?.silent) toast("Itinerary updated");
      } catch {
        setItineraries(prev);
        toast("Could not save — change reverted", "info");
      }
    },
    [itineraries, employees, canEditItinerary, canEditEmployee, joinMeta, toast],
  );

  const removeItinerary = useCallback(
    async (id: number) => {
      const existing = itineraries.find((i) => i.id === id);
      if (!canEditItinerary(existing ?? { employeeId: 0 })) {
        toast("Not authorized to remove this itinerary", "warn");
        return;
      }
      setItineraries((cur) => cur.filter((i) => i.id !== id));
      try {
        await api(`/api/routecore/itineraries/${id}`, { method: "DELETE" });
        toast("Itinerary removed", "info");
      } catch {
        toast("Could not delete", "info");
        load();
      }
    },
    [itineraries, canEditItinerary, load, toast],
  );

  /* --------------------------------- modals -------------------------------- */

  const openEmployeeModal = useCallback(
    (employee?: Employee) => setModal({ kind: "employee", employee: employee ?? null }),
    [],
  );
  const openItineraryModal = useCallback(
    (opts?: {
      itinerary?: Itinerary;
      presetDate?: string;
      presetEmployeeId?: number;
      returnToDay?: string;
    }) =>
      setModal({
        kind: "itinerary",
        itinerary: opts?.itinerary ?? null,
        presetDate: opts?.presetDate,
        presetEmployeeId: opts?.presetEmployeeId,
        returnToDay: opts?.returnToDay,
      }),
    [],
  );
  const openDayModal = useCallback((date: string) => setModal({ kind: "day", date }), []);
  const openTeamModal = useCallback(() => setModal({ kind: "teams" }), []);

  /* --------------------------------- teams --------------------------------- */

  const addTeam = useCallback(
    async (data: TeamFormData) => {
      if (!isPrivileged) {
        toast("Not authorized to create teams", "warn");
        return;
      }
      const row = await api<Team>("/api/routecore/teams", { method: "POST", body: JSON.stringify(data) });
      setTeams((cur) => [...cur, row]);
      toast(`Team “${row.name}” created`);
    },
    [isPrivileged, toast],
  );

  const updateTeam = useCallback(
    async (id: number, data: TeamFormData) => {
      if (!isPrivileged) {
        toast("Not authorized to edit teams", "warn");
        return;
      }
      const row = await api<Team>(`/api/routecore/teams/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      setTeams((cur) => cur.map((t) => (t.id === id ? row : t)));
      toast("Team updated");
    },
    [isPrivileged, toast],
  );

  const removeTeam = useCallback(
    async (id: number) => {
      if (!isPrivileged) {
        toast("Not authorized to delete teams", "warn");
        return;
      }
      const target = teams.find((t) => t.id === id);
      await api(`/api/routecore/teams/${id}`, { method: "DELETE" });
      setTeams((cur) => cur.filter((t) => t.id !== id));
      // Members fall back to unassigned (ON DELETE SET NULL).
      setEmployees((cur) => cur.map((e) => (e.teamId === id ? { ...e, teamId: null } : e)));
      setItineraries((cur) => cur.map((i) => (i.teamId === id ? { ...i, teamId: null } : i)));
      toast(`Team “${target?.name ?? ""}” disbanded`, "info");
    },
    [teams, isPrivileged, toast],
  );

  /* ------------------------------ derived stats ----------------------------- */

  const now = new Date();
  const monthCount = useMemo(() => {
    const s = iso(startOfMonth(now));
    const e = iso(endOfMonth(now));
    return itineraries.filter((i) => i.date >= s && i.date <= e).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraries]);

  const ctx: StudioCtx = {
    employees,
    itineraries,
    teams,
    currentUser,
    isPrivileged,
    canEditEmployee,
    canEditItinerary,
    openTeamModal,
    toast,
    addEmployee,
    updateEmployee,
    removeEmployee,
    addItinerary,
    updateItinerary,
    removeItinerary,
    openEmployeeModal,
    openItineraryModal,
    openDayModal,
  };

  /* --------------------------------- render -------------------------------- */

  if (phase === "loading") {
    return <IntroCurtain show />;
  }

  if (phase === "error") {
    return (
      <div className="map-dots grid min-h-screen place-items-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="grid size-14 place-items-center rounded-2xl bg-porcelain shadow-lifted p-2 border border-line">
            <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
          </span>
          <p className="font-display text-xl text-ink">The trail went cold.</p>
          <p className="max-w-xs text-sm text-ink-soft">
            We could not reach the itinerary service. Check the connection and try again.
          </p>
          <button
            type="button"
            onClick={() => {
              setPhase("loading");
              load();
            }}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-porcelain transition-colors hover:bg-accent-deep"
          >
            <RefreshCw className="size-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const dayItems =
    modal?.kind === "day"
      ? itineraries
          .filter((i) => i.date === modal.date)
          .sort((a, b) => a.employeeId - b.employeeId || bySchedule(a, b))
      : [];

  return (
    <Ctx.Provider value={ctx}>
      <IntroCurtain show={booting} />

      <motion.div
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: booting ? 0 : 1, scale: booting ? 0.985 : 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="map-dots mx-auto flex min-h-screen w-full max-w-[1920px] items-start gap-6 px-0 lg:px-5 2xl:gap-7 2xl:px-6"
      >
        <Sidebar
          tab={tab}
          onTab={setTab}
          employees={employees}
          teams={teams}
          itineraryCount={itineraries.length}
          monthCount={monthCount}
          currentUser={currentUser}
          isPrivileged={isPrivileged}
          isSuperAdmin={isSuperAdmin}
          canEditEmployee={canEditEmployee}
          onAddEmployee={() => openEmployeeModal()}
          onEditEmployee={(e) => openEmployeeModal(e)}
          onDeleteEmployee={(id) => removeEmployee(id)}
          onManageTeams={openTeamModal}
        />

        <main className="min-w-0 flex-1 px-4 pt-0 pb-16 lg:px-0 lg:pt-5">
          <MobileNav tab={tab} onTab={setTab} />
          {employees.length > 0 ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              >
                {tab === "calendar" && <CalendarView />}
                {tab === "today" && <TodayView />}
                {tab === "reports" && <ReportsView />}
                {tab === "records" && <RecordsView />}
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className="grid min-h-[70vh] place-items-center">
              <div className="flex max-w-sm flex-col items-center gap-4 text-center">
                <span className="grid size-16 place-items-center rounded-3xl bg-porcelain shadow-lifted p-2.5 border border-line">
                  <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
                </span>
                <h2 className="font-display text-2xl font-semibold text-ink">Build your field team</h2>
                <p className="text-sm text-ink-soft">
                  {isPrivileged
                    ? "Add your first employee to start plotting itineraries across the calendar."
                    : "Ask an admin to create and bind a field profile to your account before using RouteCore."}
                </p>
                {isPrivileged ? (
                  <button
                    type="button"
                    onClick={() => openEmployeeModal()}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-porcelain shadow-card transition-all hover:bg-accent-deep hover:shadow-lifted"
                  >
                    <Plus className="size-4" /> Add employee
                  </button>
                ) : (
                  <p className="text-xs text-ink-faint">
                    🔒 Read-only preview mode — no write actions available.
                  </p>
                )}
              </div>
            </div>
          )}
        </main>
      </motion.div>

      {/* overlays */}
      {(() => {
        const editingEmp = modal?.kind === "employee" ? modal.employee : null;
        const empReadOnly = !!editingEmp && !canEditEmployee(editingEmp);
        const allowCreate = isPrivileged;
        const actuallyOpen = modal?.kind === "employee" && (editingEmp ? true : allowCreate);
        return (
          <EmployeeModal
            open={actuallyOpen}
            employee={editingEmp}
            usedColors={employees.map((e) => e.color)}
            teams={teams}
            employees={employeeAccounts}
            readOnly={empReadOnly}
            isPrivileged={isPrivileged}
            onManageTeams={openTeamModal}
            onClose={() => setModal(null)}
            onSave={async (data) => {
              const editing = editingEmp;
              setModal(null);
              if (editing) await updateEmployee(editing.id, data);
              else await addEmployee(data);
            }}
          />
        );
      })()}

      <TeamModal
        open={modal?.kind === "teams" && isPrivileged}
        teams={teams}
        employees={employees}
        onClose={() => setModal(null)}
        onCreate={(data) => addTeam(data)}
        onUpdate={(id, data) => updateTeam(id, data)}
        onDelete={(id) => removeTeam(id)}
      />

      <ItineraryModal
        open={modal?.kind === "itinerary"}
        itinerary={modal?.kind === "itinerary" ? modal.itinerary : null}
        presetDate={modal?.kind === "itinerary" ? modal.presetDate : undefined}
        presetEmployeeId={modal?.kind === "itinerary" ? modal.presetEmployeeId : undefined}
        employees={employees}
        itineraries={itineraries}
        canEditEmployee={canEditEmployee}
        isPrivileged={isPrivileged}
        onClose={() => setModal(null)}
        onSave={async (data) => {
          const editing = modal?.kind === "itinerary" ? modal.itinerary : null;
          const returnToDay = modal?.kind === "itinerary" ? modal.returnToDay : undefined;
          setModal(returnToDay ? { kind: "day", date: returnToDay } : null);
          if (editing) await updateItinerary(editing.id, data);
          else await addItinerary(data);
        }}
      />

      <DayModal
        open={modal?.kind === "day"}
        date={modal?.kind === "day" ? modal.date : null}
        items={dayItems}
        employees={employees}
        isPrivileged={isPrivileged}
        canEditEmployee={canEditEmployee}
        canEditItinerary={canEditItinerary}
        onClose={() => setModal(null)}
        onAdd={() => {
          if (modal?.kind === "day") {
            openItineraryModal({ presetDate: modal.date, returnToDay: modal.date });
          }
        }}
        onEdit={(item) => {
          if (modal?.kind === "day") {
            openItineraryModal({ itinerary: item, returnToDay: modal.date });
          }
        }}
        onDelete={(item) => removeItinerary(item.id)}
      />

      <Toasts toasts={toasts} />
    </Ctx.Provider>
  );
}
